import * as React from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  H5,
  Intent,
  Callout,
  Drawer,
  TextArea,
  FormGroup,
} from "@blueprintjs/core";
import { Spacer } from "components/common/";
import { ClassNames } from "@emotion/core";
import { useMachine } from "@xstate/react";
import { zonedTimeToUtc, getTimezoneOffset } from "date-fns-tz";
import { addMilliseconds } from "date-fns";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { FormAttachment, Loading } from "components/common";
import * as turf from "@turf/helpers";
import turfCenter from "@turf/center";
import { getStartDate, getEndDate, getCoords } from "utils";
import SurveyDetails from "./SurveyDetails";
import { useSinkState, useSinkActions } from "context/";
import ImageUpload from "../ImageUpload";
import orderMachine from "./machines/orderMachine";
import ApiOrders from "../../api/orders";
import ApiSurveys from "api/surveys";
import useSurveyOrder from "./hooks/useSurveyOrder";
import { useAuthState } from "../../context/auth";
import { formatTimeAndYYMMDD } from "../../utils/format";
import { isValidCoordinates } from "utils/mapping/coords";

const getDatesForImages = (images: any[], defaultDate: Date) => {
  if (images.length > 0) {
    const startTime = getStartDate(images);
    const endTime = getEndDate(images);

    return {
      nominalDate: new Date(startTime),
      startTime,
      endTime,
    };
  }

  return { nominalDate: defaultDate, startTime: null, endTime: null };
};

const getCoordsForImages = (
  images: any[],
  defaultCoordinates: { coordinates: number[] }
) => {
  if (images.length > 0) {
    const collection = turf.featureCollection(
      images.map((image: any) => turf.point(getCoords(image) as turf.Position))
    );

    const center = turfCenter(collection);

    return {
      latitude: center.geometry.coordinates[1],
      longitude: center.geometry.coordinates[0],
    };
  }

  return {
    latitude: defaultCoordinates.coordinates[1],
    longitude: defaultCoordinates.coordinates[0],
  };
};

const CreateOrder = () => {
  const params: {
    id: string;
    type: "basic" | "thermal" | "inventory" | "custom";
  } = useParams();

  const { type } = params;

  const { sink, surveys } = useSinkState();

  const survey = surveys.find((survey) => survey.id === Number(params.id));

  const order =
    sink.orders.find((order) => order.survey_id === survey.id) || null;

  const [state, send] = useMachine(orderMachine, {
    context: { id: Number(params.id) || null, orderId: order.id },
  });

  const images = React.useMemo(
    () => Object.values(state.context.images),
    [state.context.images]
  );

  const { currentUser } = useAuthState();

  const { resink } = useSinkActions();

  const [uploadInProgress, setUploadInProgress] = React.useState(false);
  const [submittingOrder, setSubmittingOrder] = React.useState(false);
  const [confirmOrder, setConfirmOrder] = React.useState(false);
  const [showTimezoneAlert, setShowTimezoneAlert] = React.useState(false);
  const [showExistingOrderAlert, setShowExistingOrderAlert] = React.useState(
    order !== null && order.created_by !== currentUser.id
  );
  const [showCoordinatesAlert, setShowCoordinatesAlert] = React.useState(false);

  const [description, setDescription] = React.useState("");
  const [files, setFiles] = React.useState([]);

  const navigate = useNavigate();

  const {
    nominalDate,
    startTime,
    endTime,
    timezone,
    center,
    zoom,
    setDetails,
    setCenter,
    setZoom,
    setTimezone,
    setDate,
    setStartTime,
    setEndTime,
  } = useSurveyOrder();

  const site = sink.sites.find((site) => site.id === survey.site_id);

  const {
    coordinates: [lng, lat],
  } = (survey && survey.location) ||
    (site && site.location) ||
    sink.organization.location || {
      coordinates: [-123.13059, 49.2887],
    };

  React.useEffect(() => {
    const { nominalDate, startTime, endTime } = getDatesForImages(
      images,
      new Date(Date.now())
    );

    const { latitude, longitude } = getCoordsForImages(images, {
      coordinates: [lng, lat],
    });

    setDetails({
      nominalDate,
      startTime,
      endTime,
      center: [longitude, latitude],
    });
  }, [images, lng, lat]);

  const convertToSiteTime = React.useCallback(
    (date) => {
      if (!date) return null;

      const siteOffset = getTimezoneOffset(timezone, date);
      const localOffset = getTimezoneOffset(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        date
      );

      /* Date is displayed in the users local timezone, but it represents that time in the survey timezone.
         We add the difference betweent the users current timezone and their survey timezone, then convert it to UTC.
      */
      const offsetDate = addMilliseconds(date, localOffset - siteOffset);

      return zonedTimeToUtc(
        offsetDate,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    },
    [timezone]
  );

  const closeDrawer = () => {
    if (uploadInProgress) return;
    navigate(`/surveys/${params.id}`);
  };

  const submitOrder = React.useCallback(
    (source: "auto" | "manual") => {
      const nominal = convertToSiteTime(nominalDate);
      const start = convertToSiteTime(startTime);
      const end = convertToSiteTime(endTime);

      setSubmittingOrder(true);

      ApiSurveys.updateSurvey(survey.id, {
        nominal_flight_date: nominal && nominal.toISOString(),
        start_time: start && start.toISOString(),
        end_time: end && end.toISOString(),
        timezone,
        location: JSON.stringify(
          turf.point([parseFloat(center[0]), parseFloat(center[1])]).geometry
        ),
        zoom_start: Math.round(zoom),
        log: `submitOrder: ${timezone}, ${center[0]}, ${center[1]}, ${zoom}`,
      })
        .then(() =>
          ApiOrders.updateOrder(survey.id, {
            description,
            files,
            source,
          })
        )
        .then(resink)
        .then(() => {
          navigate(`/surveys/${survey.id}`);
        });
    },
    [
      nominalDate,
      startTime,
      endTime,
      timezone,
      center,
      zoom,
      survey,
      type,
      description,
      files,
      resink,
      convertToSiteTime,
    ]
  );

  const canClose = submittingOrder === false && uploadInProgress === false;

  const title = {
    basic: "Upload & Order Mapping",
    inventory: "Upload & Order Mapping and Inventory Measurement",
    custom: "Custom Request",
  };

  if (order === null || order.submitted !== null || order.type !== type) {
    return <Navigate to={`/surveys/${survey.id}`} />;
  }

  return (
    <ClassNames>
      {({ css }) => (
        <Drawer
          icon="cloud-upload"
          isOpen
          size="100%"
          position="bottom"
          className="bp6-dark"
          title={title[params.type]}
          onClose={closeDrawer}
          canOutsideClickClose={canClose}
          canEscapeKeyClose={canClose}
          isCloseButtonShown={canClose}
        >
          <section
            css={css`
              overflow-y: auto;
              display: flex;
              flex-direction: column;
              height: 100%;
            `}
          >
            {state.matches("images.ready") ? (
              <>
                <div
                  css={css`
                    padding: 1rem;
                    margin-bottom: 1rem;
                  `}
                >
                  <ImageUpload
                    survey={state.context.survey}
                    uploaderService={state.children.imageUploader}
                    setUploadInProgress={setUploadInProgress}
                    center={center}
                    setCenter={setCenter}
                    zoom={zoom}
                    timezone={timezone}
                    showTimezoneAlert={setShowTimezoneAlert}
                    showCoordinatesAlert={setShowCoordinatesAlert}
                    setZoom={setZoom}
                    setDate={setDate}
                    setStartTime={setStartTime}
                    setEndTime={setEndTime}
                    autoComplete={() => submitOrder("auto")}
                  />
                </div>
                <div
                  css={css`
                    display: flex;
                    background-color: var(--twilight);
                    padding: 1rem;
                    height: 100%;
                  `}
                >
                  <div
                    css={css`
                      width: 50%;
                    `}
                  >
                    <SurveyDetails
                      survey={survey}
                      date={nominalDate}
                      images={images}
                      setDate={setDate}
                      timezone={timezone}
                      setTimezone={setTimezone}
                      start={startTime}
                      end={endTime}
                      setStartTime={setStartTime}
                      setEndTime={setEndTime}
                      setCenter={setCenter}
                      center={center}
                    />
                  </div>
                  <div
                    css={css`
                      width: 50%;
                    `}
                  >
                    <H5>Order Details</H5>
                    <FormGroup
                      label="Description"
                      labelFor="details-input"
                      labelInfo="(optional)"
                    >
                      <TextArea
                        fill
                        size="large"
                        onChange={(event) => setDescription(event.target.value)}
                        value={description}
                        placeholder="Description"
                      />
                    </FormGroup>
                    <FormAttachment
                      files={files}
                      setFiles={(files) =>
                        setFiles((existing) => [...existing, ...files])
                      }
                    />
                    <ButtonGroup fill>
                      <Button
                        size="large"
                        disabled={submittingOrder}
                        onClick={closeDrawer}
                      >
                        Cancel
                      </Button>
                      <Button
                        intent="success"
                        size="large"
                        disabled={
                          uploadInProgress ||
                          submittingOrder ||
                          nominalDate === null
                        }
                        onClick={() => {
                          if (!timezone) {
                            setShowTimezoneAlert(true);
                            return;
                          }

                          if (!isValidCoordinates(center[1], center[0])) {
                            setShowCoordinatesAlert(true);
                            return;
                          }
                          setConfirmOrder(true);
                        }}
                      >
                        Submit Order
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              </>
            ) : (
              <Loading />
            )}
          </section>
          <Alert
            className="bp6-dark"
            css={css`
              max-width: 768px;
              width: 768px;
            `}
            cancelButtonText="Cancel & Review"
            confirmButtonText="Confirm & Submit"
            icon="send-to-map"
            intent={Intent.SUCCESS}
            loading={submittingOrder}
            isOpen={confirmOrder}
            onCancel={() => setConfirmOrder(false)}
            onConfirm={() => submitOrder("manual")}
          >
            <p>
              Work will start on this order immediately; you will not be able to
              alter this order once submitted. Please check that order uploads
              and details are complete and correct.
            </p>
            <Spacer />
            <Callout intent="warning">
              Note, files with "PENDING UPLOAD" status have{" "}
              <b>not been uploaded to our servers</b>. They are staged on your
              local browser so that they can be previewed, as the upload can
              take some time. In the map preview, yellow dots indicate "added"
              files and green dots indicate "uploaded" files. Press the "Click
              to Upload" button to upload the images.
            </Callout>
          </Alert>
          <Alert
            className="bp6-dark"
            css={css`
              max-width: 768px;
              width: 768px;
            `}
            confirmButtonText="Ok"
            icon="send-to-map"
            intent={Intent.WARNING}
            isOpen={showTimezoneAlert}
            onConfirm={() => setShowTimezoneAlert(false)}
          >
            <p>
              We are unable to detect a timezone for this survey because the
              images cross a timezone boundary.
            </p>
            <Spacer />
            <p>
              Please select a timezone or remove any images that are not part of
              this survey.
            </p>
            <Spacer />
            <Callout intent="warning">
              Orders are disabled until a time zone is selected or images are
              removed. Please re-enable the "automatically submit order when the
              upload is completed" setting if desired.
            </Callout>
          </Alert>
          <Alert
            className="bp6-dark"
            css={css`
              max-width: 768px;
              width: 768px;
            `}
            confirmButtonText="Ok"
            icon="send-to-map"
            intent={Intent.WARNING}
            isOpen={showCoordinatesAlert}
            onConfirm={() => setShowCoordinatesAlert(false)}
          >
            <p>
              We have detected invalid coordinates for this survey {center[0]},
              {center[1]}. The valid range for latitude is -90 to 90 degrees,
              and for longitude is -180 to 180 degrees.
            </p>
            <Spacer />
            <p>
              Please review the coordinates and ensure they are within the valid
              range.
            </p>
            <Spacer />
            <Callout intent="warning">
              Orders are disabled until valid coordinates are selected. Please
              re-enable the "automatically submit order when the upload is
              completed" setting if desired.
            </Callout>
          </Alert>
          {order && (
            <Alert
              className="bp6-dark"
              css={css`
                max-width: 768px;
                width: 768px;
              `}
              cancelButtonText="Cancel"
              confirmButtonText="Proceed"
              icon="send-to-map"
              intent={Intent.SUCCESS}
              isOpen={showExistingOrderAlert}
              onCancel={() => navigate(-1)}
              onConfirm={() => setShowExistingOrderAlert(false)}
            >
              <p>
                This order was created by {order.created_by_name} at{" "}
                {formatTimeAndYYMMDD(order.started)}. You can continue the order
                process and submit it yourself, or exit the order without
                submitting.
              </p>
            </Alert>
          )}
        </Drawer>
      )}
    </ClassNames>
  );
};

export default CreateOrder;
